import { cn } from "@/lib/utils";
import { SkillShikshyaIcon } from "@assets/icons";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@components/ui/accordion";
import { Button } from "@components/ui/button";
import { Popover, PopoverTrigger } from "@components/ui/popover";
import { Spinner } from "@components/ui/spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import { PopoverContent } from "@radix-ui/react-popover";
import { endpoint } from "@services/api.services";
import { METHODS, SelectProps } from "@services/interface.service";
import { useQueryClient } from "@tanstack/react-query";
import { FormSkeleton } from "@ui/external/components/skeleton";
import Tooltip from "@ui/external/components/tooltip";
import DateTime from "@ui/internal/components/date-time";
import { FormLabel } from "@ui/internal/components/label";
import { getInputField } from "@utils/function/index.function";
import {
  changeSymbolstoDash,
  // decryptData,
  isEditorContentEmpty,
  makeSlug,
  showToast,
  throwErrorAlert,
} from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { FormField } from "@utils/interface/form.interface";
import { BlogFormData, BlogSchema } from "@utils/interface/form.schema";
import { IndividualQuery } from "@utils/interface/index.interface";
import { BlogData, CourseState } from "@utils/interface/journey.interface";
import { BLOG_TYPE_OPTIONS } from "@utils/constants/blog";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import { formatISO } from "date-fns";
// import Cookies from "js-cookie";
import { ChevronLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

const index = () => {
  const queryClient = useQueryClient();
  const { handleAction, searchParams } = useHandleAction({});
  const pageItem = searchParams.get("page-item");
  const pageMode = searchParams.get("page");
  const pathname = usePathname();
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    watch,
    reset,
    formState: { errors },
  } = useForm<BlogFormData>({
    mode: "onChange",
    resolver: zodResolver(BlogSchema),
  });
  useEffect(() => {
    if (pageMode !== "edit") {
      setValue("slug", changeSymbolstoDash(makeSlug(watch("title"))));
    }
  }, [watch("title")]);
  console.log("🚀 ~ index ~ errors:", errors);
  const [dateTimeData, setDateTimeData] = useState([]);
  const dateTimeObject: CourseState = dateTimeData[0];

  /* set default value */
  const { setIsRetrive }: any = usePending();
  const { setIsFormPending } = useSelectionStore();
  const [time, setTime] = useState<string | Date | null>(null);

  console.log("dateTimeObject", time);
  console.log("this is all of the data in blogs", watch());
  console.log("This is the blogs errors", errors);

  useEffect(() => {
    console.log("dateTimeObject", dateTimeObject);
    if (dateTimeObject?.date && dateTimeObject?.startTime) {
      const combinedDateTime = new Date(
        `${dateTimeObject.date} ${dateTimeObject.startTime}`,
      );

      // Format as YYYY-MM-DDTHH:mm:ss
      const formatAsISO = (date: Date) => {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        const hours = date.getHours().toString().padStart(2, "0");
        const minutes = date.getMinutes().toString().padStart(2, "0");
        const seconds = date.getSeconds().toString().padStart(2, "0");

        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };

      const formattedTime = formatAsISO(combinedDateTime);
      setTime(formattedTime); // This will be "2025-08-28T08:00:00"
    } else {
      setTime(null);
    }
  }, [dateTimeObject]);

  const { data: fetchData, isPending: fetchPending } = useListData<
    IndividualQuery<BlogData>
  >({
    endpoint: endpoint["blog"],
    id: pageItem || "",
    key: "blog" + pageItem,
    enabled: pageItem ? true : false,
  });
  let blogTitle = fetchData?.data?.title ?? "Blogs";
  useEffect(() => {
    setIsRetrive(pageItem ? fetchPending : false);

    if (pageMode == "add") {
      return reset({});
    }

    if (fetchData && pageMode !== "add") {
      // @ts-ignore
      setStatus(fetchData?.data?.status);
      setTime(fetchData?.data?.publish_date as string);
      return reset({
        featured_image: fetchData?.data?.featured_image,
        title: fetchData?.data?.title,
        category: fetchData?.data?.category?.map((item) => ({
          label: item.name,
          value: item.id,
        })),
        slug: fetchData?.data.slug,
        description: fetchData?.data?.description,

        embedded_code: isEditorContentEmpty(fetchData?.data?.embedded_code)
          ? ""
          : fetchData?.data?.embedded_code,
        excerpt: fetchData?.data?.excerpt,
        header_code: isEditorContentEmpty(fetchData?.data?.header_code)
          ? ""
          : fetchData?.data?.header_code,
        meta_author: fetchData?.data?.meta_author,
        meta_description: fetchData?.data?.meta_description,
        meta_keywords: fetchData?.data?.meta_keywords,
        meta_title: fetchData?.data?.meta_title,
        tags: fetchData?.data?.tags?.map((item) => ({
          label: item.name,
          value: item.id,
        })),
        author_type: [
          {
            label:
              fetchData?.data?.author_type
                ?.split("_")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ") || "",
            value: fetchData?.data?.author_type,
          },
        ],
        mentor: fetchData?.data?.mentor
          ? [
              {
                label: (fetchData?.data?.mentor as any)?.full_name,
                value: (fetchData?.data?.mentor as any)?.id,
              },
            ]
          : undefined,
        team_member: fetchData?.data?.team_member
          ? [
              {
                label: (fetchData?.data?.team_member as any)?.full_name,
                value: (fetchData?.data?.team_member as any)?.id,
              },
            ]
          : undefined,
        blog_type: fetchData?.data?.blog_type
          ? [
              {
                label: `${
                  fetchData?.data?.blog_type?.charAt(0).toUpperCase() +
                  fetchData?.data?.blog_type?.slice(1)
                }`,
                value: `${fetchData?.data?.blog_type.toLowerCase()}`,
              },
            ]
          : [],
      });
    }
  }, [fetchData, reset]);

  /* mutate action */

  type BlogStatus = "publish" | "scheduled" | "draft";
  const [status, setStatus] = useState<BlogStatus>("publish");

  const handleMutate = (data: any) => {
    setIsFormPending(true);

    if (pageItem) {
      data = {
        ...data,
        id: pageItem,
      };
      // if (
      //   !(
      //     data.featured_image instanceof File ||
      //     data.featured_image instanceof Blob
      //   )
      // )
      //   delete data.featured_image;
    }

    data = {
      ...data,
      status: status === "publish" ? "published" : status,
      category: JSON.stringify(
        data["category"]?.map((item: SelectProps) => item.value),
      ),
      publish_date: time ? time : formatISO(new Date()),
      tags: JSON.stringify(
        data["tags"]?.map((item: SelectProps) => item.value),
      ),
      blog_type: data["blog_type"][0].value || "default",
    };

    if (data["author_type"]) {
      if (data["author_type"][0]?.value == "logged_in") {
        data.team_member = null;
        data.mentor = null;
      }
      data.author_type = data.author_type[0].value;
    }

    if (data["mentor"]) {
      data.mentor = data.mentor[0].value == "" ? null : data.mentor[0].value;
      data.team_member = null;
    }
    if (data["team_member"]) {
      data.team_member =
        data.team_member[0].value == "" ? null : data.team_member[0].value;
      data.mentor = null;
    }
    // console.log("Data", data);
    return {
      data,
      endpoint: endpoint["blog"],
      method: pageItem ? METHODS.PUT : METHODS.POST,
    };
  };
  const handleSuccess = async ({ status }: { data: any; status: number }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });
      setIsFormPending(false);
      showToast({
        description: `Blog ${status === 200 ? "Updated" : "Created"}`,
        type: "success",
        title: "Blog",
      });
      handleAction({ type: "page-close" });
      navigate("/blogs");
    } else {
      return new Error("Unknown API Error");
    }
  };
  const handleError = async (error: any) => {
    console.log(error, "error00");
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }
    setIsFormPending(false);
    throwErrorAlert(error);
  };

  const { mutate, isPending } = usePostData();

  const onSubmit: SubmitHandler<BlogFormData> = (data) => {
    //Add Mutation Data
    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const formSchema: FormField[] = [
    {
      name: "title",
      label: "Blog Title",
      colSpan: 12,
      type: "text",
      placeholder: "Enter Title",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "slug",
      label: "Blog Slug",
      colSpan: 12,
      type: "text",
      placeholder: "Enter Slug",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "description",
      label: "Blog Description",
      colSpan: 12,
      type: "editor",
      placeholder: "Description  here",
      control: control,
      setValue: setValue,
      height: 900,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "meta_title",
      label: "Meta Title",
      colSpan: 12,
      type: "text",
      placeholder: "Enter Title",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "meta_description",
      label: "Meta Description",
      colSpan: 12,
      type: "text",
      placeholder: "Enter Description",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "meta_keywords",
      label: "Meta Keywords (Optional)",
      colSpan: 12,
      type: "text",
      placeholder: "Enter Keywords",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "meta_author",
      label: "Meta Author",
      colSpan: 12,
      type: "text",
      placeholder: "Enter Author",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "author_type",
      label: "Author Type",
      colSpan: 12,
      type: "async",
      option: [
        {
          label: "Logged In",
          value: "logged_in",
        },
        {
          label: "Team Member",
          value: "team_member",
        },
        {
          label: "Mentor",
          value: "mentor",
        },
      ],
      placeholder: "Select Author Type",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },

    {
      name: "mentor",
      label: "Select Mentor",
      accordian_label: "Mentor",
      colSpan: 12,
      type: "async",
      endpoint: endpoint["mentor"],
      settings: { add: true, update: true, remove: true, debounce: 1000 },
      format: {
        key: "full_name",
        value: "id",
        mutate: "name",
        secondary_key: "email",
      },
      isMulti: false,
      placeholder: "Select Mentor",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: watch("author_type")
        ? (watch("author_type") as any)[0]?.value === "mentor"
        : false,
    },
    {
      name: "team_member",
      label: "Team Member",
      accordian_label: "Team Member",
      colSpan: 12,
      type: "async",
      endpoint: endpoint["team-member"],
      settings: { add: true, update: true, remove: true, debounce: 1000 },

      format: {
        key: "full_name",
        value: "id",
        mutate: "name",
        secondary_key: "email",
      },
      isMulti: false,
      placeholder: "Select Mentor",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: watch("author_type")
        ? (watch("author_type") as any)[0]?.value === "team_member"
        : false,
    },
    {
      name: "header_code",
      label: "Header code",
      colSpan: 12,
      type: "textarea",
      placeholder: "Header code  here",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "embedded_code",
      label: " Embedded Code",
      colSpan: 12,
      type: "textarea",
      placeholder: "Embedded Code  here",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
  ];
  const sidebarSchema: FormField[] = [
    {
      name: "featured_image",
      accordian_label: "Post Settings",
      label: "Featured Image",
      colSpan: 12,
      type: "cropper",
      placeholder: "Enter Title",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "details",
      errors,
      accordian_label: "Details",
      colSpan: 12,
      type: "component",
      component: (
        <div className="flex flex-col gap-2">
          <div className="flex items-center capitalize justify-between">
            <FormLabel label="Status" /> {status ?? "draft"}
          </div>
          <div className="flex items-center justify-between">
            <FormLabel label="Publish" />{" "}
            {time ? new Date(time).toLocaleString() : "Immediately"}
          </div>
          <div className="flex items-center justify-between">
            <FormLabel label="Author" /> {fetchData?.data.user.email ?? "Admin"}
          </div>
        </div>
      ),
    },
    {
      name: "excerpt",
      accordian_label: "Excerpt",
      label: "Write An Excerpt",
      colSpan: 12,
      type: "textarea",
      placeholder: "Enter Keywords",
      control: control,
      setValue: setValue,
      showLength: true,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "tags",
      label: "Add Tags",
      accordian_label: "Tags",
      colSpan: 12,
      type: "async",
      endpoint: endpoint["blog-tags"],
      settings: { add: true, update: true, remove: true, debounce: 1000 },

      format: { key: "name", value: "id", mutate: "name" },
      isMulti: true,
      placeholder: "Enter Tags",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "category",
      accordian_label: "Categories",
      colSpan: 12,
      type: "async",
      endpoint: endpoint["blog-category"],
      isMulti: true,
      format: { key: "name", value: "id", mutate: "name" },
      settings: { add: true, update: true, remove: true, debounce: 1000 },
      placeholder: "Enter Keywords",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "blog_type",
      accordian_label: "Blog Type",
      label: (
        <>
          Blog Type{" "}
          <span className="text-slate-300 font-[400] text-[12px] select-none">
            (optional)
          </span>{" "}
        </>
      ),
      colSpan: 12,
      type: "async",
      format: { key: "name", value: "id" },
      option: BLOG_TYPE_OPTIONS,
      placeholder: "Select Blog Type",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
  ];
  const [_, setOpenAccordions] = useState<string[]>([
    "featured_image",
    "excerpt",
    "tags",
    "category",
    "details",
  ]);

  console.log("errors", errors);
  console.log("Status", status);

  useEffect(() => {
    const erroredSections: string[] = [];
    for (const field of sidebarSchema) {
      if (field.name && errors[field.name as keyof BlogFormData]) {
        erroredSections.push(field.name);
      }
    }
    setOpenAccordions(erroredSections);
  }, [errors]);
  const [popoverOpen, setPopoverOpen] = useState(false);

  if (fetchPending && pageItem)
    return (
      <>
        <FormSkeleton />
      </>
    );
  const convertISOToDateTimeObject = (isoString: string) => {
    const date = new Date(isoString);

    // Extract date in YYYY-MM-DD format
    const dateStr = date.toISOString().split("T")[0];

    // Convert to 12-hour format
    const timeStr = date.toLocaleString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return {
      startTime: timeStr,
      endTime: "",
      date: dateStr,
    };
  };
  return (
    <div className="overflow-y-auto  max-h-[100vh]">
      <header className="flex shadow-md px-[30px] py-4 items-center justify-between">
        <span className="flex items-center gap-2">
          <ChevronLeft
            className="cursor-pointer"
            onClick={() => {
              navigate("/blogs");
            }}
          />
          <span
            onClick={() => {
              navigate("/journey");
            }}
            className="max-md:hidden cursor-pointer"
          >
            <SkillShikshyaIcon />
          </span>
        </span>
        <Tooltip description={blogTitle ?? "Add Blogs"}>
          <h1 className=" font-[500] capitalize max-w-[150px] truncate ">
            <>
              {" "}
              {console.log("ASdasdasd", watch("description"))}{" "}
              {blogTitle ?? " Add Blogs"}
            </>
          </h1>
        </Tooltip>
        <div className="flex gap-2">
          {status === "draft" && (
            <Button
              form="blog"
              variant={"ghost"}
              onClick={() => {
                setStatus("draft");
              }}
            >
              {"Save"} Draft
            </Button>
          )}

          <Button
            variant={status === "draft" ? "destructive" : "ghost"}
            onClick={() => {
              status === "draft" ? setStatus("publish") : setStatus("draft");
            }}
          >
            {status === "draft" ? "Cancel" : "Save"} Draft
          </Button>
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <Button variant={"outline"}>Schedule Post</Button>
            </PopoverTrigger>
            <PopoverContent className="bg-white p-4 z-4 border shadow-md">
              <Controller
                name={"publish_date"}
                control={control}
                render={({ field: { onChange, value, ...restFields } }) => (
                  <DateTime
                    isMulti={false}
                    dateTime={dateTimeData}
                    setDateTime={setDateTimeData}
                    value={
                      (value as any) || [
                        convertISOToDateTimeObject(time as string),
                      ]
                    }
                    onChange={onChange}
                    additionalFunctions={() => {
                      setStatus("scheduled");
                    }}
                    open={popoverOpen}
                    setOpen={setPopoverOpen}
                    {...restFields}
                  />
                )}
              />
            </PopoverContent>
          </Popover>
          <Button
            disabled={isPending}
            form="blog"
            endIcon={isPending ? <Spinner /> : null}
            onClick={() => {
              status === "draft" && setStatus("publish");
            }}
          >
            {isPending
              ? pageItem
                ? "Updataing..."
                : "Publishing..."
              : status == "draft"
                ? "Publish Draft"
                : "Publish"}
          </Button>
        </div>
      </header>
      <main className="p-[30px]">
        {" "}
        <form
          id="blog"
          onSubmit={handleSubmit(onSubmit)}
          className="row  w-full"
        >
          <div className="col-md-9">
            {" "}
            {formSchema.map(
              (field, index) =>
                (field.visable || field.visable == undefined) && (
                  <div
                    key={index}
                    className={`col-lg-${field.colSpan}  col-12 lg:px-[12px] px-[8px]`}
                  >
                    {getInputField({ ...field })}
                  </div>
                ),
            )}
          </div>
          <div className="col-md-3 col-12">
            {" "}
            {sidebarSchema.map(
              (field, index) =>
                (field.visable || field.visable == undefined) && (
                  <div
                    key={index}
                    className={`col-lg-${field.colSpan}  col-12 lg:px-[12px] px-[8px]`}
                  >
                    <Accordion
                      defaultValue={[
                        "featured_image",
                        "details",
                        "excerpt",
                        "tags",
                        "category",
                      ]}
                      type="multiple"
                      // value={openAccordions}
                      // onValueChange={setOpenAccordions}
                    >
                      <AccordionItem value={field.name || `accordion-${index}`}>
                        {/* @ts-ignore */}
                        <AccordionTrigger
                          className="border p-4 rounded-none "
                          chevronColor="text-[#000000]"
                        >
                          {field.accordian_label}
                        </AccordionTrigger>
                        <AccordionContent
                          className={cn(" p-4 ", "border rounded-none")}
                        >
                          {getInputField({ ...field })}
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                ),
            )}
          </div>
        </form>
      </main>
    </div>
  );
};

export default index;
